// Telemetry event contract — shared between frontend (recordEvent/pushTelemetryEvent)
// and backend (POST /telemetry/event → recordClientEvent → D1 client_events).
//
// 19BL Session 1 · Error Observability Hub foundation.
//
// Before this file, the telemetry shape was defined TWICE — once in the Hono
// endpoint (packages/ingest/.../telemetry-event.ts) and once on the frontend
// (packages/web/src/api/telemetry.ts TelemetryEventInput) — violating the
// single-source-of-truth rule (health-check §F7). This module is now the one
// authority both sides import.
//
// Two additions over the pre-19BL shape:
//   1. `id` (optional, client-generated) — enables idempotent INSERT OR IGNORE
//      on the server and lets the user reference an error by a stable handle
//      (`错误号 abcd1234`) the instant it happens, before any round-trip.
//   2. `ErrorDiagnosisSchema` — the structured "complete diagnostic packet"
//      that error-typed events carry in `payload`. Designed to fit D1's
//      payload_json TEXT ≤2000 chars constraint (stack/nav truncated upstream).
import { z } from 'zod';
// ── Event types ──────────────────────────────────────────────────────────────
/**
 * Error-typed events — the subset that the observability hub treats as
 * "actionable errors" (vs feedback flag/rating/note, or benign autosave/nav).
 * The admin Errors view (Session 5) filters on exactly this set, and the
 * agent error-feed reader (Session 5) reads only these.
 */
export const ERROR_EVENT_TYPES = [
    'runtime-error',
    'render-error',
    'unhandled-rejection',
    'autosave-error',
    'api-error', // 19BL new: covers HTTP failures the unified API client catches (Session 3)
    'behavior-error', // 19BM new: non-fatal component behavior failures previously swallowed by try/catch (regen merge / autosave commit / rollback / PDF parse, etc.) — the "observability blind-spot" the user reported
];
// ── ErrorDiagnosis · the complete diagnostic packet ──────────────────────────
/**
 * Structured context for an error event. Lives inside TelemetryEventInput.payload
 * (serialized to client_events.payload_json, ≤2000 chars after truncation).
 *
 * Design goal: enough for the agent to form a preliminary hypothesis WITHOUT
 * reading source — what view, what user action triggered it, what HTTP call
 * (if any), and the stack. Each field is independently optional because not
 * every error source has every field (a render-error has no httpStatus; an
 * api-error has no componentStack).
 *
 * String caps are chosen so a maximally-populated packet stays well under the
 * 2000-char payload_json budget: stack ≤1200 + recentNav ≤400 + the fixed
 * fields (~300 worst case) ≈ 1900. `truncateErrorDiagnosis` (below) enforces.
 */
export const ErrorDiagnosisSchema = z.object({
    /** Stable error handle, e.g. `err_01HXYZ...`. Matches the event id. */
    errorId: z.string().min(1).max(80),
    /** Stable code if known (IngestErrorCode / HTTP-derived / 'unknown'). */
    code: z.string().max(64).optional(),
    /** HTTP status if this was an API failure; absent for render/runtime errors. */
    httpStatus: z.number().int().min(100).max(599).optional(),
    /** Request URL (path + query, not full origin) for API errors. */
    url: z.string().max(500).optional(),
    method: z.string().max(10).optional(),
    /** URL pathname the user was on when the error fired. */
    viewPath: z.string().max(500).optional(),
    /** Human description of what the user did (e.g. "点了内容生成"). */
    userAction: z.string().max(200).optional(),
    /** Last few route changes, oldest→newst, to reconstruct the path to the error. */
    recentNav: z.array(z.string().max(200)).max(8).optional(),
    /** Error stack (truncated upstream to ≤1200 chars). */
    stack: z.string().max(1200).optional(),
    /** React componentStack for render-errors (truncated). */
    componentStack: z.string().max(800).optional(),
    /** ms epoch when the error occurred (frontend clock). */
    occurredAt: z.number().int().nonnegative(),
    /** App build version (__APP_VERSION__) — ties an error to a deploy. */
    appVersion: z.string().max(40).optional(),
});
// ── Truncation helpers (keep payload_json within D1 budget) ──────────────────
/**
 * Frontend truncation budget for the serialized ErrorDiagnosis payload.
 *
 * Two-layer design (2026-07-01 G3 fix):
 *   - Frontend (truncateErrorDiagnosis): trims to THIS budget (2000) so the
 *     high-signal fixed fields (errorId/code/httpStatus/...) always survive and
 *     JSON.stringify is structurally valid.
 *   - Backend (recorder.ts payload_json slice): hard-caps at 16000 as a safety
 *     net for any payload that bypasses frontend truncation (e.g. legacy
 *     callers, future non-ErrorDiagnosis payloads). 16000 > 2000, so a properly
 *     truncated ErrorDiagnosis is never re-sliced on the server.
 *
 * History: pre-2026-07-01 both layers were 2000 but the frontend layer was
 * never invoked (error-boundary sliced stack to 4000 raw → exceeded 2000 →
 * recorder sliced mid-JSON → loadErrorFeed JSON.parse threw → errorId lost).
 * The G3 fix wired the frontend layer; the backend cap was raised to 16000.
 */
export const ERROR_DIAGNOSIS_MAX_JSON_CHARS = 2000;
/**
 * Truncate an ErrorDiagnosis in place so JSON.stringify stays under the D1
 * payload_json budget. Strategy: trim the two biggest fields (stack,
 * componentStack) progressively, then recentNav. Fixed fields (errorId/code/
 * httpStatus/...) are never trimmed — they're small and high-signal.
 *
 * Returns the SAME object (mutated) for caller convenience. Idempotent: running
 * it twice is a no-op.
 */
export function truncateErrorDiagnosis(d) {
    // Start from schema caps. If already under budget, no-op.
    const initial = JSON.stringify(d);
    if (initial.length <= ERROR_DIAGNOSIS_MAX_JSON_CHARS)
        return d;
    // Phase 1: halve componentStack first (render-errors only, often huge).
    if (d.componentStack && d.componentStack.length > 200) {
        d.componentStack = d.componentStack.slice(0, Math.floor(d.componentStack.length / 2));
    }
    if (JSON.stringify(d).length <= ERROR_DIAGNOSIS_MAX_JSON_CHARS)
        return d;
    // Phase 2: halve stack.
    if (d.stack && d.stack.length > 200) {
        d.stack = d.stack.slice(0, Math.floor(d.stack.length / 2));
    }
    if (JSON.stringify(d).length <= ERROR_DIAGNOSIS_MAX_JSON_CHARS)
        return d;
    // Phase 3: drop recentNav entries from the front (keep most-recent).
    if (d.recentNav && d.recentNav.length > 1) {
        d.recentNav = d.recentNav.slice(-2);
    }
    if (JSON.stringify(d).length <= ERROR_DIAGNOSIS_MAX_JSON_CHARS)
        return d;
    // Phase 4: hard cut stack to whatever budget remains. Last resort.
    const over = JSON.stringify(d).length - ERROR_DIAGNOSIS_MAX_JSON_CHARS;
    if (d.stack && d.stack.length > over + 50) {
        d.stack = d.stack.slice(0, Math.max(50, d.stack.length - over - 10));
    }
    return d;
}
// ── TelemetryEventInput · the wire contract for POST /telemetry/event ────────
/**
 * Single source of truth for the POST /telemetry/event body. Supersedes the
 * duplicate Zod schema that lived in the endpoint and the TS interface on the
 * frontend.
 *
 * `id` is optional: when the frontend generates it (error events, Session 2),
 * the server uses INSERT OR IGNORE for idempotency. When omitted (legacy /
 * non-error events), the server generates one (preserves backwards compat).
 */
export const TelemetryEventInputSchema = z.object({
    /** Client-generated stable id (enables idempotency + user-referenceable error handle). */
    id: z.string().min(1).max(80).optional(),
    type: z.string().min(1).max(64),
    summary: z.string().min(1).max(500),
    courseId: z.string().max(120).optional(),
    courseTitle: z.string().max(200).optional(),
    itemId: z.string().max(120).optional(),
    itemField: z.string().max(120).optional(),
    itemType: z.string().max(64).optional(),
    itemPreview: z.string().max(2000).optional(), // server truncates to 400
    viewPath: z.string().max(500).optional(),
    /** Free-form payload. For error events, this is a serialized ErrorDiagnosis. */
    payload: z.record(z.unknown()).optional(),
    flagged: z.boolean().optional(),
    rating: z.number().int().min(1).max(5).optional(),
    note: z.string().max(2000).optional(), // server truncates to 500
});
//# sourceMappingURL=telemetry.js.map